import { t } from '@/shared/i18n';
import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import s from './ui.module.css';

export function Card({ title, children }: { readonly title?: string | undefined; readonly children: ReactNode }) {
  return (
    <section className={s.card} aria-label={title}>
      {title !== undefined && <h2 className={s.cardTitle}>{title}</h2>}
      {children}
    </section>
  );
}

export function Stack({ children }: { readonly children: ReactNode }) {
  return <div className={s.stack}>{children}</div>;
}

export function Row({ children }: { readonly children: ReactNode }) {
  return <div className={s.row}>{children}</div>;
}

export function PageHeader({ title, actions }: { readonly title: string; readonly actions?: ReactNode }) {
  return (
    <header className={s.pageHeader}>
      <h1 className={s.pageTitle}>{title}</h1>
      {actions !== undefined && <div className={s.pageActions}>{actions}</div>}
    </header>
  );
}

/**
 * Пустой экран: что здесь появится и что сделать. Всегда по центру свободной области, чуть выше середины
 * (страница, колонка «Выберите …», панель); в блоке без своей высоты — просто с отступами сверху.
 * Свои «пустые» блоки с иконкой и текстом не заводим — только этот компонент.
 */
export function EmptyState({ icon, title, children, action }: { readonly icon?: IconName; readonly title?: string; readonly children?: ReactNode; readonly action?: ReactNode }) {
  return (
    <div className={s.empty}>
      {icon !== undefined && (
        <span className={s.emptyIcon}>
          <Icon name={icon} size={28} />
        </span>
      )}
      {title !== undefined && <p className={s.emptyTitle}>{title}</p>}
      {children !== undefined && <div className={s.emptyText}>{children}</div>}
      {action}
    </div>
  );
}

/** Загрузка: аккуратное кольцо по центру области. `inline` — внутри кнопки, значка, ряда: без отступов. */
export function Spinner({ label = t('Загрузка'), inline = false }: { readonly label?: string; readonly inline?: boolean }) {
  return (
    <span role="status" aria-label={label} className={inline ? s.spinnerInline : s.spinnerBlock}>
      <span className={s.spinner} aria-hidden="true" />
    </span>
  );
}

/** Загрузка списка — серые силуэты строк на месте будущих (как в Notion и Slack): ничего не прыгает, когда приходят данные. */
export function ListSkeleton({ rows = 8, label = t('Загрузка') }: { readonly rows?: number; readonly label?: string }) {
  return (
    <div role="status" aria-label={label} className={s.skelList}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={s.skelRow} aria-hidden="true">
          <span className={`${s.bone} ${s.boneIcon}`} />
          <span className={s.boneText}>
            <span className={s.bone} style={{ width: `${62 - (i % 4) * 9}%` }} />
            <span className={`${s.bone} ${s.boneSub}`} style={{ width: `${38 - (i % 3) * 6}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

export function Badge({ children }: { readonly children: ReactNode }) {
  return <span className={s.badge}>{children}</span>;
}
